/**
 * Fan info als één inline bewerkbaar paneel (15-10-2026, Jordy).
 *
 * Vervangt de vensters "Fan info" (Edit/Add info) en "Important notes" in de
 * rechterkolom. Alles opslaan via de BESTAANDE routes:
 *   - een fan_info-veld      PATCH /conversations/{id}/fan-profile  {info: {sleutel: waarde}}
 *     (de server voegt samen: alleen deze sleutel verandert, de rest blijft)
 *   - een eigen veld          PATCH .../fan-profile {info: {_custom_fields: [...hele lijst]}}
 *   - de belangrijke notities PATCH .../important-notes {notes}
 *   - de bijnaam              PATCH .../fan-dashboard-name {display_name}
 *
 * Indeling: vast bovenaan bijnaam + echte naam, tijdzone, laatst actief,
 * status. Daaronder alle overige velden: INGEVULD eerst, LEEG daaronder (blijft
 * invulbaar). De volgorde komt uit de opgeslagen gegevens, dus een veld schuift
 * pas omhoog na het opslaan, niet tijdens het typen.
 *
 * Geen React: de tests voeren dit uit met node (esbuild).
 */

export type Info = Record<string, unknown>

export interface VeldDef { key: string; label: string; meerregelig?: boolean }

/** De vaste velden uit het oude Edit-venster, in die volgorde. */
export const FAN_VELDEN: VeldDef[] = [
  { key: 'name', label: 'Name / Username (real)' },
  { key: 'age', label: 'Age' },
  { key: 'location', label: 'Location / Country' },
  { key: 'timezone', label: 'Timezone' },
  { key: 'work', label: 'Work / Studies' },
  { key: 'relationship', label: 'Relationship status' },
  { key: 'hobbies', label: 'Hobbies / Lifestyle' },
  { key: 'preferences', label: 'Preferences (general)' },
  { key: 'interests', label: 'Personal interests' },
  { key: 'enjoys_in_chats', label: 'What he enjoys in chats', meerregelig: true },
  { key: 'important_events', label: 'Important events', meerregelig: true },
  { key: 'memory_points', label: 'Memory points', meerregelig: true },
]

/** Hetzelfde veld onder een andere sleutel. Het oude dashboard schreef
 *  `chat_preferences` (TT: 4 fans, 15-10); v2 `enjoys_in_chats`. We lezen en
 *  schrijven de sleutel waar de waarde al staat, zodat er niets dubbel komt. */
export const ALIAS: Record<string, string[]> = { enjoys_in_chats: ['chat_preferences'] }

/** Vast bovenaan, los van de sortering. */
export const VAST = new Set(['name', 'timezone'])

export interface Regel {
  /** Uniek in het paneel: "info:age", "eigen:0", "notes". */
  id: string
  soort: 'info' | 'eigen' | 'notes'
  /** fan_info-sleutel (soort info) of de index in _custom_fields (soort eigen). */
  sleutel: string
  label: string
  waarde: string
  meerregelig: boolean
}

export function alsTekst(v: unknown): string {
  if (Array.isArray(v)) return v.map((x) => alsTekst(x)).filter(Boolean).join(', ')
  if (v == null || typeof v === 'object') return ''
  return String(v)
}

function mensLabel(k: string): string {
  return k.replace(/_/g, ' ').replace(/^./, (x) => x.toUpperCase())
}

/** De sleutel waaronder dit vaste veld zijn waarde heeft (of zou krijgen). */
export function sleutelVoor(info: Info | null | undefined, key: string): string {
  const i = info ?? {}
  if (alsTekst(i[key]).trim()) return key
  for (const oud of ALIAS[key] ?? []) if (alsTekst(i[oud]).trim()) return oud
  return key
}

export function eigenVelden(info: Info | null | undefined): { label: string; value: string }[] {
  const lijst = (info ?? {})._custom_fields
  return Array.isArray(lijst)
    ? lijst.map((x) => ({
        label: String((x as { label?: unknown } | null)?.label ?? ''),
        value: String((x as { value?: unknown } | null)?.value ?? ''),
      }))
    : []
}

function infoRegel(info: Info, def: VeldDef): Regel {
  const s = sleutelVoor(info, def.key)
  return { id: `info:${def.key}`, soort: 'info', sleutel: s, label: def.label,
           waarde: alsTekst(info[s]), meerregelig: !!def.meerregelig }
}

/** Ingevuld eerst, leeg daaronder; binnen elke groep de vaste volgorde. */
export function sorteer(regels: Regel[]): Regel[] {
  const vol = regels.filter((r) => r.waarde.trim())
  const leeg = regels.filter((r) => !r.waarde.trim())
  return [...vol, ...leeg]
}

export interface Indeling {
  /** Vast bovenaan: echte naam en tijdzone (bijnaam, laatst actief en status komen van het gesprek). */
  naam: Regel
  tijdzone: Regel
  /** De rest, gesorteerd: ingevuld eerst. */
  overig: Regel[]
}

export function indeling(info: Info | null | undefined, notities: string | null | undefined): Indeling {
  const i = info ?? {}
  const bekend = new Set<string>([...FAN_VELDEN.map((v) => v.key), ...Object.values(ALIAS).flat()])
  const regels: Regel[] = [
    { id: 'notes', soort: 'notes', sleutel: 'notes', label: 'Important notes',
      waarde: String(notities ?? ''), meerregelig: true },
    ...FAN_VELDEN.filter((d) => !VAST.has(d.key)).map((d) => infoRegel(i, d)),
    // Sleutels die het oude venster niet kende (language, kinks, ...): blijven
    // zichtbaar en bewerkbaar, met een leesbaar label.
    ...Object.keys(i)
      .filter((k) => !k.startsWith('_') && !bekend.has(k))
      .filter((k) => i[k] == null || typeof i[k] !== 'object' || Array.isArray(i[k]))
      .map((k): Regel => ({ id: `info:${k}`, soort: 'info', sleutel: k, label: mensLabel(k),
                             waarde: alsTekst(i[k]), meerregelig: false })),
    ...eigenVelden(i).map((v, n): Regel => ({ id: `eigen:${n}`, soort: 'eigen', sleutel: String(n),
                                               label: v.label || 'Custom field', waarde: v.value,
                                               meerregelig: false })),
  ]
  return {
    naam: infoRegel(i, FAN_VELDEN[0]),
    tijdzone: infoRegel(i, FAN_VELDEN[3]),
    overig: sorteer(regels),
  }
}

export interface Opslag { pad: string; body: unknown }

/** Waar en hoe één gewijzigd veld wordt opgeslagen (bestaande routes). */
export function opslagVoor(conversationId: number | string, info: Info | null | undefined,
                           regel: Regel, waarde: string): Opslag {
  const basis = `/api/admin/conversations/${conversationId}`
  if (regel.soort === 'notes') return { pad: `${basis}/important-notes`, body: { notes: waarde } }
  if (regel.soort === 'eigen') {
    const lijst = eigenVelden(info)
    const n = Number(regel.sleutel)
    if (lijst[n]) lijst[n] = { ...lijst[n], value: waarde }
    return { pad: `${basis}/fan-profile`, body: { info: { _custom_fields: lijst } } }
  }
  return { pad: `${basis}/fan-profile`, body: { info: { [regel.sleutel]: waarde } } }
}

/** "Add info": een nieuw eigen veld achteraan de bestaande lijst. */
export function nieuwEigenVeld(conversationId: number | string, info: Info | null | undefined,
                               label: string, waarde: string): Opslag | null {
  const l = label.trim()
  if (!l) return null
  return {
    pad: `/api/admin/conversations/${conversationId}/fan-profile`,
    body: { info: { _custom_fields: [...eigenVelden(info), { label: l, value: waarde }] } },
  }
}

/** Een eigen veld verwijderen (de rest van de lijst blijft). */
export function eigenVeldWeg(conversationId: number | string, info: Info | null | undefined,
                             index: number): Opslag {
  return {
    pad: `/api/admin/conversations/${conversationId}/fan-profile`,
    body: { info: { _custom_fields: eigenVelden(info).filter((_, n) => n !== index) } },
  }
}
